import { Alert } from '@/src/common/components/elements/Forms/Alert'
import { DialogTitle } from '@/src/common/components/elements/Modals/Modal'
import { CUSTOM_CARD_IMAGE } from '@/src/common/constants'
import { styled } from '@/src/stitches.config'
import {
    faArrowRotateRight,
    faMagnifyingGlassMinus,
    faMagnifyingGlassPlus,
    faRotateLeft,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { BaseSyntheticEvent, useEffect, useRef, useState } from 'react'
import Button from '../../../common/components/marketing/buttons/Button'
import usePostCloudinaryCustomImage from '../../../common/hooks/usePostCloudinaryCustomImage'
import CustomDesignCropper from '../CustomDesignCropper'
import {
    CARD_HEIGHT_DEFAULT,
    CARD_WIDTH_DEFAULT,
    CUSTOM_IMAGE_TYPE,
} from '../constants'
import DefaultModal from '@/src/common/components/marketing/modals/DefaultModal'
import { ModalButtonsWrapper } from '@/src/common/components/elements/Modals/StyledModalComponents'
import { usePartnerTheme } from '@/src/common/contexts/PartnerThemeContext'

const IconButton = styled('div', {
    pt: '5px',
    '& svg': {
        my: 10,
        ml: 10,
        color: '$secondary',
        fontSize: '30px',
    },
})

const FAIcon = styled(FontAwesomeIcon, {
    color: '$primary',
    mr: '0.5rem',
    my: '15px',
})
const ErrorContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '50vh',
    alignItems: 'center',
})
const ModalContent = styled('div', {
    display: 'flex',
    gap: 10,
    fd: 'column',
    width: '100%',
    '@media (min-width: 655px)': {
        fd: 'row',
        gap: 40,
    },
})
const LeftWrapper = styled('div', {
    width: '100%',
    height: 'max-content',
    '@media (min-width: 655px)': {
        width: '52%',
    },
})
const RightWrapper = styled('div', {
    width: '100%',
    display: 'flex',
    fd: 'column-reverse',
    gap: 20,
    '@media (min-width: 655px)': {
        width: '48%',
        fd: 'column',
    },
})
const RightContent = styled('div', {
    gap: 5,
})

const StyledList = styled('ul', {
    mt: 5,
    mb: 0,
    pl: '2px',
    '& li': {
        listStyle: 'none',
        fontSize: '.95rem',
        pb: '5px',
        '&:before': {
            pr: '10px',
        },
    },
    variants: {
        variant: {
            deny: {
                '& li': {
                    '&:before': {
                        content: '⊘',
                        color: '$error',
                    },
                },
            },
            approve: {
                '& li:before': {
                    content: '✓',
                    color: '$success',
                },
            },
            warning: {
                '& li:before': {
                    content: '⚠',
                    color: '#8D7007',
                    pr: 5,
                },
                '& li.no-show-bullet': {
                    '&:before': {
                        display: 'none',
                    },
                },
            },
        },
    },
})

const StyledH2 = styled(DialogTitle, {
    fontSize: '1.2rem',
    color: '$dark',
    fontWeight: 'bold',
    letterSpacing: 1,
    mb: 5,
})

const Text = styled('p', {
    fontWeight: 'bold',
    fontSize: '$4',
    lineHeight: '1.5 !important',
    my: 5,
    '& a': {
        color: '$secondary',
        textDecoration: 'underline',
    },
    variants: {
        variant: {
            small: {
                fontSize: '13px !important',
                paddingLeft: 20,
                fontWeight: 'normal',
            },
            green: {
                color: '$success',
                fontSize: '15px',
                fontWeight: 'lighter',
            },
            red: {
                color: '$error',
                fontSize: '15px',
                fontWeight: 'lighter',
            },
            warning: {
                color: '#8D7007',
                fontSize: '15px',
                fontWeight: 'lighter',
            },
            image: {
                fontWeight: 'normal',
                fontSize: '15px !important',
                textAlign: 'center !important',
                lineHeight: '1.5 !important',
            },
        },
    },
})

const ImageWrapper = styled('div', {
    maxWidth: '100%',
    flexWrap: 'wrap',
    display: 'flex',
    fd: 'row',
    ai: 'center',
    jc: 'center',
})

export interface ICustomImageModal {
    isOpen: boolean
    setIsOpen: Function
    setDesignImage: Function
    setCustomDesignObject: Function
    setPredesignUuid: Function
    setPlaceholderClicked?: Function | null
    setDesignName: Function
    setSelectedImageType: Function
}

export const CustomImageModal: React.FC<ICustomImageModal> = ({
    isOpen,
    setIsOpen,
    setDesignImage,
    setCustomDesignObject,
    setPredesignUuid,
    setPlaceholderClicked,
    setDesignName,
    setSelectedImageType,
}) => {
    const imageInputRef = useRef<HTMLInputElement>(null)
    const { mutate, isLoading, isError } = usePostCloudinaryCustomImage()
    const [customDesignImageData, setCustomDesignImageData] = useState('') // This will always be a "data:image/..." string.
    const [cropper, setCropper] = useState<Cropper | null>(null)
    const [imageError, setImageError] = useState<string>('')
    const [imgSelected, setImgSelected] = useState<boolean>(false)
    const partnerTheme = usePartnerTheme()

    useEffect(() => {
        if (setPlaceholderClicked && !imgSelected && !isOpen) {
            setPlaceholderClicked(false)
        }
    }, [imgSelected, isOpen, setPlaceholderClicked])

    const handleImageChange = (
        event: BaseSyntheticEvent,
        callback: Function | undefined
    ) => {
        setImageError('')
        setImgSelected(true)
        // Read the file and then upload in the background
        if (event.target.files.length > 0) {
            const reader = new FileReader()
            reader.onload = (event) =>
                // @ts-ignore
                setCustomDesignImageData(event.target.result)
            reader.readAsDataURL(event.target.files[0])
        }
        if (callback && typeof callback === 'function') {
            callback()
        }
    }

    const onSaveDesignAssets = async () => {
        if (!cropper || !cropper.getCroppedCanvas()) {
            alert('This file type is not supported.')
            return
        }
        const data = cropper
            .getCroppedCanvas({
                width: CARD_WIDTH_DEFAULT,
                height: CARD_HEIGHT_DEFAULT,
            })
            .toDataURL()
        await mutate(data, {
            onSuccess: (data) => {
                setCustomDesignObject(data)
                setDesignImage(data.url)
                setPredesignUuid('') // Clear the predesign UUID
                setDesignName(CUSTOM_CARD_IMAGE)
                setIsOpen(false)
                setImgSelected(true)
                setSelectedImageType(CUSTOM_IMAGE_TYPE)
            },
            onError: () => {
                setImageError(
                    'Image upload failed. Please try again or select a new image'
                )
            },
        })
    }

    return (
        <DefaultModal
            showModal={isOpen}
            setShowModal={setIsOpen}
            testId={'custom_image_modal'}
            onOpenAutoFocus={() => {
                if (!customDesignImageData) {
                    imageInputRef.current?.click()
                }
            }}
            width={'medium'}
        >
            <ModalContent className={partnerTheme}>
                <LeftWrapper>
                    {imageError && (
                        <ErrorContainer>
                            <Alert variant={'error'} label={imageError} />
                        </ErrorContainer>
                    )}
                    <ImageWrapper>
                        {customDesignImageData && (
                            <CustomDesignCropper
                                imageFile={customDesignImageData}
                                setCropper={setCropper}
                            />
                        )}
                        <IconButton
                            onClick={() => cropper?.zoom(0.1)}
                            color="primary"
                        >
                            <FAIcon
                                icon={faMagnifyingGlassPlus}
                                fontSize="large"
                            />
                        </IconButton>
                        <IconButton
                            onClick={() => cropper?.zoom(-0.1)}
                            color="primary"
                        >
                            <FAIcon
                                icon={faMagnifyingGlassMinus}
                                fontSize="large"
                            />
                        </IconButton>
                        <IconButton
                            onClick={() => cropper?.rotate(-90)}
                            color="primary"
                        >
                            <FAIcon icon={faRotateLeft} fontSize="large" />
                        </IconButton>
                        <IconButton
                            onClick={() => cropper?.rotate(90)}
                            color="primary"
                        >
                            <FAIcon
                                icon={faArrowRotateRight}
                                fontSize="large"
                            />
                        </IconButton>
                    </ImageWrapper>
                    <Text variant={'image'}>
                        Move the preview box to select your gift card
                        background. Photos must adhere to our{' '}
                        <a href={'/image-guidelines'}>image guidelines</a>{' '}
                        provided by Visa or Mastercard.
                    </Text>
                </LeftWrapper>
                <RightWrapper>
                    <RightContent>
                        <StyledH2>Adjust your photo:</StyledH2>
                        <Text variant={'green'}>
                            Your photo will be approved if it&apos;s:
                        </Text>
                        <StyledList variant={'approve'}>
                            <li>Less than 10MB</li>
                            <li>A personal photo that you own the rights to</li>
                        </StyledList>
                        <Text variant={'warning'}>
                            Your photo may require additional verification if it
                            contains:
                        </Text>
                        <StyledList variant={'warning'}>
                            <li>Company names or logos</li>
                            <li className={'no-show-bullet'}>
                                <Text variant={'small'}>
                                    <a href={'/corporate/bulk'}>Click here</a>{' '}
                                    to add your business logo or order in bulk
                                    for orders larger than 50 cards or with more
                                    than $10,000 in load value!
                                </Text>
                            </li>
                        </StyledList>
                        <Text variant={'red'}>
                            Your photo will be rejected if it contains:
                        </Text>
                        <StyledList variant={'deny'}>
                            <li>Celebrities, athletes, cartoons</li>
                            <li>
                                Anti-government behavior, violence, or nudity
                            </li>
                            <li>Alcohol, tobacco, gambling, or firearms</li>
                            <li>
                                Numbers, texts, or images that might cause
                                confusion
                            </li>
                        </StyledList>
                    </RightContent>
                    <ModalButtonsWrapper gap={'5px'} marginTop={'none'}>
                        <input
                            type="file"
                            accept={'image/png, image/webp, image/jpeg'}
                            style={{ display: 'none' }}
                            // @ts-ignore
                            onChange={handleImageChange}
                            required
                            ref={imageInputRef}
                            id="image-file-upload"
                        />
                        {imgSelected && (
                            <Button
                                label={
                                    isLoading
                                        ? 'Uploading...'
                                        : 'Use this photo'
                                }
                                disabled={isLoading || isError}
                                onClick={onSaveDesignAssets}
                                showArrow={true}
                                isLoading={isLoading}
                                width={'standard'}
                            />
                        )}
                        <Button
                            label={'Upload a photo'}
                            variant={
                                !imgSelected ? 'primary' : 'primaryOutline'
                            }
                            onClick={() => imageInputRef.current?.click()}
                            width={'standard'}
                            tabIndex={0}
                            disabled={isLoading}
                        />
                    </ModalButtonsWrapper>
                </RightWrapper>
            </ModalContent>
        </DefaultModal>
    )
}
